import { ParsedDate, toParsedDate } from '@api/Trinium/types';
import { parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';

/** Every per-statement detail endpoint is addressed the same way: /:terminalNumber/statements/:statementId/... */
export interface SettlementDetailRequest {
  terminalNumber: string;
  statementId: number;
}

export interface SettlementStatementRecord {
  statementId: number;
  statementDate: ParsedDate;
  /** Billing week the statement covers, pre-formatted "MM/DD/YYYY - MM/DD/YYYY" by the API. */
  billingWeek: string;
}

export interface SettlementStatementRaw {
  /** Supplier Invoice Sequence (supinvoice.siv_seq) */
  id: number;
  /** supinvoice.siv_chk_date — the Thursday the check is issued. */
  date: string;
  /** Pre-formatted "MM/DD/YYYY - MM/DD/YYYY". */
  billingWeek?: string;
}

export function transformSettlementStatementResponse(response: SettlementStatementRaw[]): SettlementStatementRecord[] {
  return response
    .flatMap((item) => {
      const statementDate = toParsedDate(parseDayJs(item.date));

      // A statement with no usable date cannot be ordered, so it is not offered for selection.
      if (!statementDate) return [];

      return [{ statementId: item.id, statementDate, billingWeek: item.billingWeek ?? '' }];
    })
    // The API returns statements in supplier invoice sequence order; the selector lists newest first.
    .sort((a, b) => b.statementDate.unix - a.statementDate.unix);
}

/*
 * The settlement detail endpoints serialize with WRITE-JSON's omit-initial-values on, so a field
 * holding 0 or "" is dropped from the row entirely rather than sent. Those fields are optional on
 * the raw shapes and restored to their initial value by the transforms.
 */

/** One service charge on one load (costleg). `id` is the costleg sequence, unique per row. */
export interface SettlementLoadRaw {
  id: number;
  jobId: number;
  costDate: string;
  custId: string;
  custName: string;
  custCity: string;
  custState: string;
  cntrId?: string;
  chassisId?: string;
  svcId: string;
  svcName: string;
  commissionAmt?: number;
  terminalNumber: string;
  puName: string;
  puCity: string;
  puState: string;
  delvName: string;
  delvCity: string;
  delvState: string;
  notes?: string;
}

export interface SettlementLoadRecord {
  id: number;
  loadNo: number;
  /** Always served today; optional only because an unparseable date narrows to undefined. */
  costDate?: ParsedDate;
  serviceCode: string;
  serviceDescription: string;
  notes: string;
  commission: number;
  container: string;
  chassis: string;
  puName: string;
  puCity: string;
  puState: string;
  dlvName: string;
  dlvCity: string;
  dlvState: string;
  custId: string;
  custName: string;
  custCity: string;
  custState: string;
}

export function transformSettlementLoadsResponse(response: SettlementLoadRaw[]): SettlementLoadRecord[] {
  return response.map((row) => ({
    id: row.id,
    loadNo: row.jobId,
    costDate: toParsedDate(parseDayJs(row.costDate)),
    serviceCode: row.svcId,
    serviceDescription: row.svcName,
    notes: row.notes ?? '',
    commission: row.commissionAmt ?? 0,
    container: row.cntrId ?? '',
    chassis: row.chassisId ?? '',
    puName: row.puName,
    puCity: row.puCity,
    puState: row.puState,
    dlvName: row.delvName,
    dlvCity: row.delvCity,
    dlvState: row.delvState,
    custId: row.custId,
    custName: row.custName,
    custCity: row.custCity,
    custState: row.custState
  }));
}

/** One invoiced charge in the billing week. `id` is the charge sequence, unique per row. */
interface BillingChargeRaw {
  id: number;
  jobId: number;
  custId: string;
  custName: string;
  svcId: string;
  svcName: string;
  cntrId?: string;
  amt?: number;
  /** Delivery date of the consignment's last leg. */
  lastDelvDate: string;
  invDate: string;
}

interface BillingByCustomerRaw {
  custId: string;
  custName: string;
  amt?: number;
}

interface BillingByServiceRaw {
  svcId: string;
  svcName: string;
  amt?: number;
}

/**
 * The billing week's pre-aggregated billingByCust/billingBySvc/billingByDate sections are not
 * typed here: the Billing Week tab derives the same totals from the lines, which it has to do
 * anyway to filter them by clicked summary row.
 */
export interface BillingWeekRaw {
  id: number;
  billing?: BillingChargeRaw[];
}

export interface BillingWeekRecord {
  id: number;
  /** Order number / job id the charge was billed on. */
  loadNo: number;
  customerId: string;
  customer: string;
  serviceCode: string;
  serviceDescription: string;
  container: string;
  chargeAmount: number;
  fullyDeliveredDate?: ParsedDate;
  invoiceDate?: ParsedDate;
}

export function transformBillingWeekResponse(response: BillingWeekRaw): BillingWeekRecord[] {
  return (response.billing ?? []).map((row) => ({
    id: row.id,
    loadNo: row.jobId,
    customerId: row.custId,
    customer: row.custName,
    serviceCode: row.svcId,
    serviceDescription: row.svcName,
    container: row.cntrId ?? '',
    chargeAmount: row.amt ?? 0,
    fullyDeliveredDate: toParsedDate(parseDayJs(row.lastDelvDate)),
    invoiceDate: toParsedDate(parseDayJs(row.invDate))
  }));
}

/** A costleg on the statement tied to neither a load nor a deduction schedule. */
export interface CommissionRaw {
  id: number;
  svcId: string;
  svcName: string;
  notes?: string;
  amt?: number;
}

export interface CommissionRecord {
  id: number;
  serviceCode: string;
  serviceDescription: string;
  notes: string;
  amount: number;
}

export function transformCommissionsResponse(response: CommissionRaw[]): CommissionRecord[] {
  return response.map((row) => ({
    id: row.id,
    serviceCode: row.svcId,
    serviceDescription: row.svcName,
    notes: row.notes ?? '',
    amount: row.amt ?? 0
  }));
}

export type DeductionType = 'One-Time' | 'Recurring';

/**
 * A costleg collected against a deduction schedule (recuraccess). The API calls a deduction
 * one-time when this week's amount equals the schedule maximum. All amounts are negative.
 */
export interface DeductionRaw {
  id: number;
  svcId: string;
  svcName: string;
  amt?: number;
  deductionType: 'oneTime' | 'recurring';
  /** recuraccess.rac_seq — the deduction schedule. */
  racId: number;
  /** The schedule maximum. */
  maxAmt?: number;
  /** Collected on earlier statements. */
  prevAmt?: number;
  /** prevAmt plus this week. */
  totalAmt?: number;
  /** Omitted once the schedule is fully collected. */
  remainingAmt?: number;
  notes?: string;
}

export interface DeductionRecord {
  id: number;
  /** Trinium's identifier for the deduction schedule. Not displayed; carried for future editing. */
  internalId: number;
  serviceCode: string;
  serviceDescription: string;
  deductionType: DeductionType;
  originalAmount: number;
  amountThisWeek: number;
  previouslyCollected: number;
  total: number;
  remaining: number;
  deductionNotes: string;
}

export function transformDeductionsResponse(response: DeductionRaw[]): DeductionRecord[] {
  return response.map((row) => ({
    id: row.id,
    internalId: row.racId,
    serviceCode: row.svcId,
    serviceDescription: row.svcName,
    deductionType: row.deductionType === 'oneTime' ? 'One-Time' : 'Recurring',
    originalAmount: row.maxAmt ?? 0,
    amountThisWeek: row.amt ?? 0,
    previouslyCollected: row.prevAmt ?? 0,
    total: row.totalAmt ?? 0,
    remaining: row.remainingAmt ?? 0,
    deductionNotes: row.notes ?? ''
  }));
}

export interface SummaryBreakdownLine {
  serviceDescription: string;
  amount: number;
}

export interface DeductionBreakdownLine {
  deductionType: DeductionType;
  serviceDescription: string;
  amount: number;
}

export interface YtdCheckRecord {
  checkDate: ParsedDate;
  amount: number;
}

/** One per-service money line. `svcId` is the Trinium service code; the UI shows only the name. */
interface SummaryServiceLineRaw {
  svcId: string;
  svcName: string;
  amt: number;
}

interface SummaryDeductionLineRaw extends SummaryServiceLineRaw {
  type: 'oneTime' | 'recurring';
}

/** One issued check. `id` is the statement it belongs to, so the selected statement's own check is findable. */
interface SummaryCheckRaw {
  id: number;
  checkDate: string;
  amt: number;
}

/**
 * The summary endpoint does not serve load commissions (nor, therefore, total commission or net
 * check). The Summary tab derives those from the jobData (loads) endpoint instead.
 *
 * The arrays are optional because the API omits a section entirely rather than sending it empty —
 * statement 831482 has no `commissionAdjBySvc` key at all.
 */
export interface SettlementSummaryRaw {
  /** Supplier Invoice Sequence (supinvoice.siv_seq) — the statement id. */
  id: number;
  date: string;
  /** Pre-formatted "MM/DD/YYYY - MM/DD/YYYY". */
  billingWeek: string;
  earningsYtd: number;
  commissionAdjBySvc?: SummaryServiceLineRaw[];
  commissionSumYtd?: SummaryCheckRaw[];
  deductionsBySvc?: SummaryDeductionLineRaw[];
}

export interface SettlementSummaryRecord {
  commissionAdjustments: number;
  commissionAdjustmentBreakdown: SummaryBreakdownLine[];
  deductions: number;
  deductionBreakdown: DeductionBreakdownLine[];
  earnings1099Ytd: number;
  ytdChecks: YtdCheckRecord[];
}

function sumAmounts(lines: { amount: number }[]) {
  return lines.reduce((total, line) => total + line.amount, 0);
}

export function transformSettlementSummaryResponse(response: SettlementSummaryRaw): SettlementSummaryRecord {
  const commissionAdjustmentBreakdown = (response.commissionAdjBySvc ?? []).map((line) => ({
    serviceDescription: line.svcName,
    amount: line.amt
  }));

  const deductionBreakdown: DeductionBreakdownLine[] = (response.deductionsBySvc ?? []).map((line) => ({
    deductionType: line.type === 'oneTime' ? 'One-Time' : 'Recurring',
    serviceDescription: line.svcName,
    amount: line.amt
  }));

  const ytdChecks = (response.commissionSumYtd ?? []).flatMap((check) => {
    // Via parseDayJs so an unparseable date narrows to undefined; toParsedDate's string overload claims it cannot.
    const checkDate = toParsedDate(parseDayJs(check.checkDate));
    // A check with no usable date can be neither labelled nor keyed, so it is not listed.
    if (!checkDate) return [];

    return [{ checkDate, amount: check.amt }];
  });

  return {
    commissionAdjustments: sumAmounts(commissionAdjustmentBreakdown),
    commissionAdjustmentBreakdown,
    deductions: sumAmounts(deductionBreakdown),
    deductionBreakdown,
    earnings1099Ytd: response.earningsYtd,
    ytdChecks
  };
}

/**
 * Billing from January 1 of the statement's year through its check date. The API serves only the
 * two aggregates — no charge lines — so per-load figures such as a load count are not available.
 */
export interface BillingYtdRaw {
  id: number;
  billingByCust?: BillingByCustomerRaw[];
  billingBySvc?: BillingByServiceRaw[];
}

export interface YtdByCustomerRecord {
  /** The customer code; names are not unique across customers. */
  id: string;
  customer: string;
  amount: number;
}

export interface YtdByServiceRecord {
  /** The service code. */
  id: string;
  serviceDescription: string;
  amount: number;
}

export interface YtdSummary {
  byCustomer: YtdByCustomerRecord[];
  byService: YtdByServiceRecord[];
}

export function transformBillingYtdResponse(response: BillingYtdRaw): YtdSummary {
  return {
    byCustomer: (response.billingByCust ?? [])
      .map((row) => ({ id: row.custId, customer: row.custName, amount: row.amt ?? 0 }))
      .sort((a, b) => b.amount - a.amount),
    byService: (response.billingBySvc ?? [])
      .map((row) => ({ id: row.svcId, serviceDescription: row.svcName, amount: row.amt ?? 0 }))
      .sort((a, b) => b.amount - a.amount)
  };
}
